import { useState, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { ChevronDown, Check } from "lucide-react";
import {
  DropdownContainer,
  DropdownTrigger,
  DropdownMenu,
  MenuItems,
  SearchInput,
  MenuItem,
  MenuFooter,
  EmptyState,
  Backdrop,
} from "./DropdownV2.styles";

// value shape: { value, label, icon? }
// for multiple: array of { value, label, icon? }

export const DropdownV2 = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  multiple = false,
  searchable = false,
  searchPlaceholder = "Search...",
  disabled = false,
  minWidth,
  maxHeight,
  menuFooter,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  const selectedValues = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }
    return value ? [value] : [];
  }, [value, multiple]);

  const isSelected = useCallback(
    (optionValue) => selectedValues.some((sv) => sv.value === optionValue),
    [selectedValues]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const query = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, search]);

  const toggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) {
        setSearch("");
        setTimeout(() => searchRef.current?.focus(), 0);
      }
      return !prev;
    });
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  const handleSelect = useCallback(
    (option) => {
      const item = { value: option.value, label: option.label };
      if (option.icon) item.icon = option.icon;

      if (multiple) {
        const exists = selectedValues.some((sv) => sv.value === option.value);
        const next = exists
          ? selectedValues.filter((sv) => sv.value !== option.value)
          : [...selectedValues, item];
        onChange(next);
      } else {
        onChange(item);
        close();
      }
    },
    [multiple, selectedValues, onChange, close]
  );

  const triggerLabel = useMemo(() => {
    if (selectedValues.length === 0) return null;

    if (multiple) {
      if (selectedValues.length === options.length) return "All selected";
      if (selectedValues.length === 1) return selectedValues[0].label;
      return `${selectedValues.length} selected`;
    }

    return selectedValues[0].label;
  }, [selectedValues, multiple, options.length]);

  return (
    <DropdownContainer className={className}>
      <DropdownTrigger onClick={toggle} disabled={disabled} type="button">
        {triggerLabel ? (
          <span className="trigger-label">{triggerLabel}</span>
        ) : (
          <span className="trigger-label trigger-placeholder">
            {placeholder}
          </span>
        )}
        <ChevronDown
          size={16}
          className={`trigger-icon ${isOpen ? "open" : ""}`}
        />
      </DropdownTrigger>

      {isOpen && (
        <>
          <Backdrop onClick={close} />
          <DropdownMenu $minWidth={minWidth} $maxHeight={maxHeight}>
            {searchable && (
              <SearchInput>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  onClick={(e) => e.stopPropagation()}
                />
              </SearchInput>
            )}

            <MenuItems>
              {filteredOptions.length === 0 ? (
                <EmptyState>No results found</EmptyState>
              ) : (
                filteredOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    $isSelected={isSelected(option.value)}
                    onClick={() => handleSelect(option)}
                  >
                    {option.icon && (
                      <span className="item-icon">
                        {typeof option.icon === "string" ? (
                          <img src={option.icon} alt="" />
                        ) : (
                          option.icon
                        )}
                      </span>
                    )}
                    <span className="item-label">{option.label}</span>
                    {isSelected(option.value) && (
                      <span className="item-check">
                        <Check size={16} />
                      </span>
                    )}
                  </MenuItem>
                ))
              )}
            </MenuItems>

            {menuFooter && (
              <MenuFooter>{menuFooter({ close, onChange })}</MenuFooter>
            )}
          </DropdownMenu>
        </>
      )}
    </DropdownContainer>
  );
};

const valuePropShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
});

DropdownV2.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    valuePropShape,
    PropTypes.arrayOf(valuePropShape),
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  minWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  menuFooter: PropTypes.func,
  className: PropTypes.string,
};
